import { devLog } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { Star, Clock, CheckCircle, Heart, Briefcase, Sparkles, Loader2, ArrowLeft } from 'lucide-react';
import { FeedbackSection } from '../components/professional/FeedbackSection';
import { QuickSessionCard, VerificationCard, AvailabilityCard } from '../components/professional/SidebarCards';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeConfig = {
  therapist: { label: 'Terapeuta', badgeClass: 'badge-therapist', icon: Heart },
  coach: { label: 'Mentor', badgeClass: 'badge-coach', icon: Briefcase },
  spiritualist: { label: 'Espiritualista', badgeClass: 'badge-spiritualist', icon: Sparkles }
};

export default function ProfessionalDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfessional();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProfessional = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/professionals/${id}`);
      setProfessional(data);
    } catch (err) {
      setError('Profissional não encontrado');
      devLog.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackSubmit = async (rating, comment) => {
    await axios.post(`${API_URL}/api/feedbacks`, { professional_id: id, rating, comment }, {
      headers: getAuthHeaders(), withCredentials: true
    });
    fetchProfessional();
  };

  const handleQuickSession = async (question) => {
    await axios.post(`${API_URL}/api/quick-sessions`, { professional_id: id, question, session_type: 'quick' }, {
      headers: getAuthHeaders(), withCredentials: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#4A5D4E] animate-spin" /></main>
        <Footer />
      </div>
    );
  }

  if (error || !professional) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center">
          <p className="text-[#B85C5C] mb-4">{error || 'Profissional não encontrado'}</p>
          <Link to="/professionals" className="btn-secondary">Voltar para listagem</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const config = typeConfig[professional.professional_type] || typeConfig.therapist;
  const TypeIcon = config.icon;

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />
      <main className="flex-1 py-12" data-testid="professional-detail-page">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link to="/professionals" className="inline-flex items-center gap-2 text-[#63635E] hover:text-[#292927] mb-8 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Voltar para listagem
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Profile Card */}
              <div className="card-theraflow p-8" data-testid="profile-card">
                <div className="flex flex-col sm:flex-row items-start gap-6">
                  <div className="relative flex-shrink-0">
                    {professional.photo_url ? (
                      <img src={professional.photo_url} alt={professional.name} className="w-32 h-32 rounded-3xl object-cover" />
                    ) : (
                      <div className="w-32 h-32 rounded-3xl bg-[#F6EBE6] flex items-center justify-center">
                        <TypeIcon className="w-16 h-16 text-[#4A5D4E]" strokeWidth={1} />
                      </div>
                    )}
                    {professional.verified && (
                      <div className="absolute -bottom-2 -right-2 bg-[#6B8E7C] rounded-full p-1.5">
                        <CheckCircle className="w-5 h-5 text-white" strokeWidth={2} />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${config.badgeClass}`}>
                        <TypeIcon className="w-4 h-4" strokeWidth={1.5} /> {config.label}
                      </span>
                      {professional.verified && <span className="text-xs text-[#6B8E7C] font-medium">Verificado</span>}
                    </div>

                    <h1 className="text-2xl sm:text-3xl font-medium text-[#292927] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {professional.name}
                    </h1>

                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`w-5 h-5 ${s <= Math.round(professional.rating_avg || 0) ? 'text-[#D4A373] fill-[#D4A373]' : 'text-[#EAE8E3]'}`} strokeWidth={1.5} />
                        ))}
                      </div>
                      <span className="text-[#292927] font-medium">{professional.rating_avg?.toFixed(1) || '0.0'}</span>
                      <span className="text-[#63635E]">({professional.rating_count || 0} avaliações)</span>
                    </div>

                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2 text-sm text-[#63635E]">
                        <Clock className="w-4 h-4" strokeWidth={1.5} /> {professional.experience_years} anos de experiência
                      </div>
                      {professional.hourly_rate && (
                        <div className="flex items-center gap-2 text-sm text-[#292927] font-medium">
                          R$ {professional.hourly_rate?.toFixed(0)}/hora
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {professional.bio && (
                  <div className="mt-8 pt-8 border-t border-[#EAE8E3]">
                    <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Sobre</h2>
                    <p className="text-[#63635E] leading-relaxed">{professional.bio}</p>
                  </div>
                )}

                {professional.specializations?.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-[#EAE8E3]">
                    <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Especializações</h2>
                    <div className="flex flex-wrap gap-2">
                      {professional.specializations.map((spec) => (
                        <span key={spec} className="px-4 py-2 bg-[#F6EBE6] text-[#292927] text-sm rounded-full">{spec}</span>
                      ))}
                    </div>
                  </div>
                )}

                {professional.approach?.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-[#EAE8E3]">
                    <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Abordagens</h2>
                    <div className="flex flex-wrap gap-2">
                      {professional.approach.map((a) => (
                        <span key={a} className="px-4 py-2 bg-[#4A5D4E]/10 text-[#4A5D4E] text-sm rounded-full">{a}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <FeedbackSection professional={professional} user={user} onFeedbackSubmit={handleFeedbackSubmit} />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <QuickSessionCard professional={professional} user={user} onSubmit={handleQuickSession} />
              <VerificationCard professional={professional} />
              <AvailabilityCard professional={professional} professionalId={id} />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
